import { Video } from 'lucide-react';
import { Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useMeetingsSetup } from '../../../lib/meetings-channel';
import { meetingsChatsEmpty } from '../../../lib/meetings-chats-empty';
import { connectorsPath } from '../../../lib/connectors-route';
import styles from './Chats.module.css';

// The same calendar row the Meetings card sends a calendar-less user to (and `settings/calendars` resolves to).
const CALENDAR_ROW = connectorsPath({ tab: 'tools', target: 'google-calendar' });
const MEETINGS_ROW = connectorsPath({ tab: 'channels', target: 'meetings' });

/**
 * The Calendar Meetings tab with nothing in it. Rather than a bare "empty", it names the one step that
 * would put a meeting here. The Connect itself stays on the connectors page, behind the card's
 * explanation lines: connecting is the consent, so it is never a one-tap button here.
 */
export function MeetingsChatsEmpty() {
  const { t } = useTranslation();
  const productName = useProductName();
  const { logPageClickBi } = useGenericBi();
  const setup = useMeetingsSetup();
  const state = meetingsChatsEmpty(setup);
  useLoadBi('chats-meetings-setup-data', { isLoading: state === 'loading', isError: false });

  if (state === 'loading') return <div role="status" className={styles.placeholder}>{t('common.loading')}</div>;
  // The same sunken card the generic zero-chats state uses (Figma 18693:74702). The two empties sit on
  // the SAME list — one bare and one carded would read as two different screens.
  const cta = state === 'connect-calendar'
    ? { body: t('chats.meetings.needCalendar.body', { productName }), label: t('chats.meetings.needCalendar.cta'), href: CALENDAR_ROW, bi: 'meetings_connect_calendar' }
    : state === 'connect-meetings'
      ? { body: t('chats.meetings.needMeetings.body', { productName }), label: t('chats.meetings.needMeetings.cta'), href: MEETINGS_ROW, bi: 'meetings_connect' }
      : null;
  return <Card className={styles.emptyCard}>
    <Video size={32} aria-hidden />
    <Typography variant="h4" align="center">{t('chats.meetings.empty')}</Typography>
    {cta && <>
      <Typography variant="p2" color="secondary" align="center">{cta.body}</Typography>
      <Button href={cta.href} onClick={() => logPageClickBi({ page_name: 'chats', button_name: cta.bi })}>{cta.label}</Button>
    </>}
  </Card>;
}
